/**
 * Блок остатков платёжных систем.
 *
 * Flow: опрос /api/treasury/psp-balances раз в 60 секунд → группировка
 *       по системе и валюте → таблица кошельков с возрастом остатка.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadPspBalances.
 *
 * Связанные файлы:
 *   - src/App.jsx — размещает блок на экране;
 *   - docs/specs/BL-2026-09-07-0a54-psp-on-screen.md — техспека.
 */

import { useCallback, useEffect, useState } from 'react'

import { loadPspBalances } from './api'

/** Период опроса. Остатки обновляются раз в пять минут — чаще смотреть незачем. */
const POLL_MS = 60000

const SOURCE_TITLES = {
  praxis: 'Praxis Cashier',
  b2binpay: 'B2BinPay',
  coinsbuy: 'Coinsbuy',
}

/** Показывает давность в человекочитаемом виде; null — «неизвестно». */
export function formatAge(minutes) {
  if (minutes === null || minutes === undefined) return '—'
  if (minutes < 60) return `${minutes} мин`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours} ч`
  return `${Math.floor(hours / 24)} сут`
}

/**
 * Показывает денежную величину.
 *
 * Пустое значение — ПРОЧЕРК, а не ноль: «остатка нет» и «остаток ноль» —
 * разные утверждения, и второе скрыло бы первое (BR-011).
 */
export function formatAmount(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return value
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 8 })
}

/**
 * Группирует кошельки по ПЛАТЁЖНОЙ СИСТЕМЕ, сохраняя порядок ответа.
 *
 * @param {Array} wallets — кошельки из ответа ручки
 * @returns {Array<{source: string, wallets: Array}>} группы по системам
 *
 * Прежде группировка шла по паре «система + валюта», и у трёх систем
 * с четырьмя валютами выходило до дюжины таблиц по одной-две строки:
 * перечень кошельков одной системы приходилось собирать глазами из
 * нескольких кусков. Теперь система — одна таблица.
 *
 * Валюта при этом НЕ ПОТЕРЯНА: она ушла к остатку, где ей и место —
 * величина без валюты бессмысленна (BR-004), и заголовок был не лучшим
 * её носителем.
 */
export function groupWallets(wallets) {
  const groups = []
  const index = new Map()
  for (const w of wallets) {
    if (!index.has(w.source_code)) {
      const group = { source: w.source_code, wallets: [] }
      index.set(w.source_code, group)
      groups.push(group)
    }
    index.get(w.source_code).wallets.push(w)
  }
  return groups
}

export default function PspBalancesPanel() {
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    loadPspBalances()
      .then((data) => {
        setReport(data)
        setError('')
      })
      .catch((e) => setError(e.message))
  }, [])

  useEffect(() => {
    load()
    const timer = setInterval(load, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  if (error) {
    return (
      <section className="card">
        <h2>Остатки платёжных систем</h2>
        <div className="error">{error}</div>
      </section>
    )
  }

  if (!report) {
    return (
      <section className="card">
        <h2>Остатки платёжных систем</h2>
        <p className="muted">Загрузка…</p>
      </section>
    )
  }

  // Блок скрывается целиком, пока ни одна платёжная система не включена.
  // Пустая таблица там, где данных не может быть по построению, — шум,
  // который учит не смотреть на блок (техспека §3.4).
  if (!report.sources || report.sources.length === 0) {
    return null
  }

  const totals = report.totals || []
  const groups = groupWallets(report.wallets || [])

  return (
    <section className="card">
      <h2>Остатки платёжных систем</h2>

      {totals.length === 0 ? (
        <p className="muted">Остатки ещё не загружены.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Система</th>
              <th>Валюта</th>
              <th className="num">Итого</th>
              <th className="num">Кошельков</th>
            </tr>
          </thead>
          <tbody>
            {totals.map((t) => (
              <tr key={`${t.source_code}-${t.currency_code}`}>
                <td>{SOURCE_TITLES[t.source_code] || t.source_code}</td>
                <td>{t.currency_code}</td>
                <td className="num">{formatAmount(t.total)}</td>
                <td className="num">
                  {t.wallets}
                  {/* Кошельки без остатка названы отдельно: итог по трём
                      кошелькам из пяти — не итог (BR-023). */}
                  {t.missing > 0 && (
                    <span className="warn" title="кошельки без остатка в итог не вошли">
                      {' '}
                      +{t.missing} без остатка
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Перечень кошельков — в растягивающейся области: блок выровнен
          по соседу в ряду, и лишняя высота достаётся перечню, а не пустоте
          под ним (см. .card-fill в styles.css). */}
      <div className="card-fill">
      {groups.map((g) => (
        <div key={g.source}>
          <h3>{SOURCE_TITLES[g.source] || g.source}</h3>
          <table>
            <thead>
              <tr>
                <th>Название</th>
                <th>Кошелёк</th>
                <th className="num">Остаток</th>
                <th>Обновлено</th>
              </tr>
            </thead>
            <tbody>
              {g.wallets.map((w) => (
                <tr key={`${w.source_code}-${w.wallet_ref}`}>
                  <td>{w.title || '—'}</td>
                  <td>{w.wallet_ref}</td>
                  {/* Валюта стоит ПРИ величине, а не в заголовке группы:
                      группировка теперь по системе, и остаток без валюты
                      был бы числом без смысла (BR-004). У кошелька без
                      снимка валюта не показывается — прочерк означает
                      «остатка нет», и приписывать ему валюту не к чему. */}
                  <td className="num">
                    {formatAmount(w.balance)}
                    {w.balance !== null && w.balance !== undefined && w.balance !== '' && (
                      <span className="muted"> {w.currency_code}</span>
                    )}
                  </td>
                  <td className={w.is_stale ? 'warn' : ''}>
                    {formatAge(w.age_minutes)}
                    {w.is_stale && ' — устарел'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      </div>
    </section>
  )
}
